import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react"
import { ChevronDown, Plus } from "lucide-react"
import {
  ComponentPropsWithoutRef,
  FocusEvent,
  KeyboardEvent,
  ReactElement,
  ReactNode,
  useMemo,
  useRef,
  useState,
} from "react"

import { classNames } from "../../../../lib/classNames"
import {
  HEADLESS_OPTIONS_LIMIT,
  isHeadlessOptionsTruncated,
  renderHeadlessSafeItems,
} from "../../../../lib/headless"
import { Icon, IconSizes } from "../../../Icon"
import { TextInput, TextInputSize } from "../text/TextInput"

export interface ComboboxProps<T>
  extends Omit<ComponentPropsWithoutRef<"div">, "onChange"> {
  label?: string
  secondaryLabel?: string
  items: T[]
  selectedItem: T | null
  onSelectItem: (item: T | null) => void
  buildItemFromNew?: (newItem: string) => T
  isCreatable?: boolean
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  filterValueExtractor?: (item: T) => string
  invalid?: boolean
  errorMessage?: string
  disabled?: boolean
  placeholder?: string
  onChangeQuery?: (query: string) => void
  autoFocus?: boolean
  hideChevronButton?: boolean
  eq?: (a: T | null, b: T | null) => boolean
  isOpenOnFocus?: boolean
  renderItemPrefix?: (a: T | null) => ReactElement
  renderItemLabel?: (a: T | null) => ReactNode
  optionsAnchor?: ComponentPropsWithoutRef<typeof ComboboxOptions>["anchor"]
  optionsClassName?: string
  inputClassName?: string
  isDeselectable?: boolean
  deselectText?: string
  isBold?: boolean
  inputSize?: TextInputSize
}

export function Combobox<T>(props: ComboboxProps<T>) {
  const [query, setQuery] = useState("")

  const {
    label,
    secondaryLabel,
    items,
    selectedItem,
    onSelectItem,
    buildItemFromNew = (newItem) => newItem,
    isCreatable,
    keyExtractor,
    valueExtractor,
    filterValueExtractor,
    invalid,
    errorMessage,
    disabled,
    placeholder,
    onChangeQuery,
    autoFocus,
    hideChevronButton = false,
    isOpenOnFocus = false,
    renderItemPrefix,
    renderItemLabel,
    eq,
    optionsAnchor,
    optionsClassName,
    inputClassName,
    isDeselectable,
    isBold,
    deselectText,
    inputSize = "md",
    ...otherProps
  } = props

  const searchExtractor = filterValueExtractor ?? valueExtractor
  const filterFn = useMemo(
    () =>
      onChangeQuery
        ? () => () => true
        : (query: string) => (item: T) =>
            searchExtractor(item).toLowerCase().includes(query.toLowerCase()),
    [onChangeQuery, searchExtractor]
  )
  const filteredItems = query === "" ? items : items.filter(filterFn(query))

  const equalityFn =
    eq ||
    ((a: T | null, b: T | null) => {
      if (a === null && b === null) return true
      if (a === null || b === null) return false
      return keyExtractor(a) === keyExtractor(b)
    })

  // While the query is empty, seed the "new item" option from a selected item
  // that isn't in items yet (typically committed on a previous open but not
  // persisted), so reopening still shows it as the pending new entry. Only
  // valid with local filtering — under onChangeQuery, items hold the last
  // search's results, so absence doesn't mean the selection is new.
  const pendingNewItemName =
    !onChangeQuery &&
    selectedItem &&
    !items.some((item) => equalityFn(item, selectedItem))
      ? valueExtractor(selectedItem)
      : ""
  const newItemName = query !== "" ? query : pendingNewItemName

  const findItemByName = (name: string) =>
    items.find(
      (item) => valueExtractor(item).toLowerCase() === name.toLowerCase()
    )

  const showNewItemOption =
    !!isCreatable && newItemName !== "" && !findItemByName(newItemName)

  const hasNoResults =
    filteredItems.length === 0 && !showNewItemOption && !isDeselectable

  // For creatable comboboxes, leaving the field with uncommitted text should
  // select it, not discard it — Headless UI's default blur behavior silently
  // resets the input to the last selected value.
  const skipBlurCommitRef = useRef(false)
  const commitTypedText = (text: string) => {
    const typed = text.trim()
    if (!typed) return
    if (selectedItem && valueExtractor(selectedItem) === typed) return
    onSelectItem(findItemByName(typed) ?? (buildItemFromNew(typed) as T))
  }
  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Tab" || !isCreatable) return
    // Commit before Headless UI's own Tab handler runs; if the user arrowed
    // to an option, that handler selects it afterwards and wins, matching
    // Enter's semantics.
    commitTypedText(event.currentTarget.value)
    skipBlurCommitRef.current = true
  }
  const onInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    const skip = skipBlurCommitRef.current
    skipBlurCommitRef.current = false
    if (skip || !isCreatable) return
    const text = event.currentTarget.value
    // Headless UI's own blur handler runs after this one and resets an empty
    // selection via onChange(null); defer the commit so it lands last.
    queueMicrotask(() => commitTypedText(text))
  }

  const defaultOptionClassName =
    "bg-neutral-default relative cursor-default select-none px-4 py-2"

  return (
    <div {...otherProps}>
      <HeadlessCombobox
        value={selectedItem}
        onChange={onSelectItem}
        disabled={disabled}
        by={equalityFn}
        multiple={false}
        immediate={isOpenOnFocus}
        onClose={() => setQuery("")}
      >
        <ComboboxInput
          as={TextInput}
          label={label}
          secondaryLabel={secondaryLabel}
          onChange={(event) => {
            setQuery(event.target.value)
            onChangeQuery?.(event.target.value)
          }}
          onKeyDown={onInputKeyDown}
          onBlur={onInputBlur}
          displayValue={(item) =>
            item ? valueExtractor(item as unknown as T) : ""
          }
          placeholder={placeholder ?? "Select one"}
          autoFocus={autoFocus}
          errorMessage={errorMessage}
          invalid={invalid}
          className={classNames(
            "block truncate",
            isBold && "font-semibold",
            inputClassName
          )}
          inputSize={inputSize}
          rightElement={
            !hideChevronButton ? (
              <ComboboxButton>
                <Icon
                  icon={ChevronDown}
                  size={IconSizes.ExtraSmall}
                  className="color-icon-default"
                  aria-hidden="true"
                />
              </ComboboxButton>
            ) : undefined
          }
        />

        <ComboboxOptions
          anchor={
            optionsAnchor ?? {
              to: "bottom",
              gap: 8,
            }
          }
          className={classNames(
            "w-(--input-width) elevation-surface-default border-v2-border-default shadow-control text-body-lg z-10 max-h-60 overflow-auto rounded-sm border [--anchor-max-height:15rem]",
            optionsClassName
          )}
          modal={false}
        >
          {hasNoResults ? (
            <div
              className={classNames(
                defaultOptionClassName,
                "text-body-lg text-center"
              )}
            >
              No results
            </div>
          ) : (
            <>
              {isDeselectable && (
                <ComboboxOption
                  value={null}
                  className={({ focus, selected }) =>
                    classNames(
                      defaultOptionClassName,
                      focus && "bg-neutral-hovered",
                      selected && "bg-selected-alternate"
                    )
                  }
                >
                  <div className="flex flex-row items-center gap-2">
                    <span className="text-body-lg block truncate">
                      {deselectText ?? "None"}
                    </span>
                  </div>
                </ComboboxOption>
              )}
              {renderHeadlessSafeItems(filteredItems).map((item) => (
                <ComboboxOption
                  key={keyExtractor(item)}
                  value={item}
                  className={({ focus, selected }) =>
                    classNames(
                      defaultOptionClassName,
                      focus && "bg-neutral-hovered",
                      selected && "bg-selected-alternate"
                    )
                  }
                >
                  <div className="flex flex-row items-center gap-2">
                    {renderItemPrefix && renderItemPrefix(item)}
                    {renderItemLabel ? (
                      renderItemLabel(item)
                    ) : (
                      <span className="text-body-lg block truncate">
                        {valueExtractor(item)}
                      </span>
                    )}
                  </div>
                </ComboboxOption>
              ))}
              {isHeadlessOptionsTruncated(filteredItems) && (
                <div
                  className={classNames(
                    defaultOptionClassName,
                    "text-body-sm color-subtle text-center"
                  )}
                >
                  Showing the first {HEADLESS_OPTIONS_LIMIT.toLocaleString()}{" "}
                  results. Type to narrow the list.
                </div>
              )}
            </>
          )}
          {showNewItemOption && (
            <ComboboxOption
              value={buildItemFromNew(newItemName)}
              className={({ focus }) =>
                classNames(
                  defaultOptionClassName,
                  focus && "bg-neutral-hovered"
                )
              }
            >
              <div className="flex flex-row items-center gap-2">
                <Icon
                  icon={Plus}
                  size={IconSizes.ExtraSmall}
                  className="color-icon-default"
                  aria-hidden="true"
                />
                <span className="text-body-lg block truncate">
                  Create new{" "}
                  <span className="color-accent-blue font-semibold">
                    &quot;{newItemName}&quot;
                  </span>
                </span>
              </div>
            </ComboboxOption>
          )}
        </ComboboxOptions>
      </HeadlessCombobox>
    </div>
  )
}
